import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { Card } from "@/components/ui/card";
import { apiGet } from "@/lib/client/api";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";

export function QuotePreviewPage() {
  const { id } = useParams();
  const { t } = useAppSettings();
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadPreview() {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const [response, detail] = await Promise.all([
          fetch(`/api/quotations/${id}/export/html`, { credentials: "include" }),
          apiGet<{ capabilities: { canEdit: boolean } }>(`/api/quotations/${id}`),
        ]);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}.`);
        }

        const body = await response.text();

        if (active) {
          setHtml(body);
          setCanEdit(detail.capabilities.canEdit);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : t("quotePreview.loadError"));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadPreview();

    return () => {
      active = false;
    };
  }, [id, t]);

  if (loading) {
    return <Card className="p-6 text-sm text-muted-foreground">{t("quotePreview.loading")}</Card>;
  }

  if (error || !html) {
    return <Card className="p-6 text-sm text-muted-foreground">{error ?? t("quotePreview.notFound")}</Card>;
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("quotePreview.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("quotePreview.description")}</p>
        </div>
        {canEdit ? <Link to={workbenchRoutes.quotationEdit(id ?? "")} className="inline-flex h-9 items-center justify-center rounded-md border border-border px-3 text-sm font-medium transition hover:bg-muted">
          {t("quotePreview.edit")}
        </Link> : null}
      </div>
      <Card className="overflow-hidden p-0">
        <iframe title={t("quotePreview.frameTitle")} srcDoc={html} className="h-[calc(100vh-150px)] w-full bg-white" />
      </Card>
    </section>
  );
}
